import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TablePager } from "../../components/shared/TablePager";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import { AdminGate } from "../../components/admin/AdminGate";
import { useToast } from "../../components/toast/toastContext";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import styles from "../../components/admin/Admin.module.css";
import { createCurrencyRate, fetchCurrencyRates, moneyUnitLabels, MoneyUnitCode, type CurrencyRate } from "../../api/currencyRates";
import { Dropdown } from "../../components/shared/Dropdown";

const SettingsWritePermission = "settings.write";

/**
 * The "Currency rates" screen (phase 21, `AIA-81`, `OQ-23`/`PD-05`): read-only list of `CurrencyRate`
 * rows plus a create form. Rates are immutable: a change is a new row with a later effective-from date
 * that supersedes earlier ones. Gated on `settings.write` rather
 * than `entitlement.administer` (see `api/currencyRates.ts` for the DTO-shape caveat around
 * `MoneyUnit` serialising as an integer, not a string).
 */
export function CurrencyRatesPage(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <AdminGate permissions={[SettingsWritePermission]} title={t("admin.currencyRates.denied.title")} description={t("admin.currencyRates.denied.description")}>
        <CurrencyRatesContent />
      </AdminGate>
    </div>
  );
}

export function CurrencyRatesContent(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; rates: CurrencyRate[] } | { kind: "error"; message: string }>({ kind: "loading" });
  const [form, setForm] = useState({ unit: String(MoneyUnitCode.Usd), targetCurrency: "USD", rate: "1", effectiveFrom: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchCurrencyRates();
    if (result.kind === "ok") {
      setState({ kind: "loaded", rates: result.response });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.currencyRates.denied.description") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    const result = await createCurrencyRate(Number(form.unit) as MoneyUnitCode, form.targetCurrency, Number(form.rate), new Date(form.effectiveFrom).toISOString());
    if (result.kind === "ok") {
      void load();
    } else {
      toast.show(result.kind === "denied" ? t("admin.denied.description", { permissions: SettingsWritePermission }) : result.message, "error");
    }
  };

  const columns = useMemo<TableColumn<CurrencyRate>[]>(
    () => [
      { id: "unit", name: t("admin.currencyRates.table.unit"), selector: (row) => moneyUnitLabels[row.unit], sortable: true },
      { id: "targetCurrency", name: t("admin.currencyRates.table.targetCurrency"), selector: (row) => row.targetCurrency, sortable: true },
      { id: "rate", name: t("admin.currencyRates.table.rate"), selector: (row) => row.rate, sortable: true },
      {
        id: "effectiveFrom",
        name: t("admin.currencyRates.table.effectiveFrom"),
        selector: (row) => row.effectiveFrom,
        sortable: true,
        cell: (row) => row.effectiveFrom.slice(0, 10),
      },
    ],
    [t],
  );

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.currencyRates.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("admin.currencyRates.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  return (
    <>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          void handleCreate();
        }}
      >
        <div className={styles.field}>
          <label htmlFor="currency-rate-unit">{t("admin.currencyRates.table.unit")}</label>
          <Dropdown
            id="currency-rate-unit"
            options={Object.entries(moneyUnitLabels).map(([code, label]) => ({ value: code, label }))}
            value={form.unit}
            onChange={(unit) => setForm({ ...form, unit })}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="currency-rate-target">{t("admin.currencyRates.table.targetCurrency")}</label>
          <input id="currency-rate-target" value={form.targetCurrency} onChange={(event) => setForm({ ...form, targetCurrency: event.target.value })} />
        </div>
        <div className={styles.field}>
          <label htmlFor="currency-rate-rate">{t("admin.currencyRates.table.rate")}</label>
          <input id="currency-rate-rate" type="number" step="0.0001" value={form.rate} onChange={(event) => setForm({ ...form, rate: event.target.value })} />
        </div>
        <div className={styles.field}>
          <label htmlFor="currency-rate-effective">{t("admin.currencyRates.table.effectiveFrom")}</label>
          <input id="currency-rate-effective" type="date" value={form.effectiveFrom} onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value })} />
        </div>
        <button type="submit" className="button button-primary" disabled={!form.targetCurrency || !form.effectiveFrom}>
          {t("admin.currencyRates.create")}
        </button>
      </form>
      {state.rates.length === 0 ? (
        <EmptyState title={t("admin.currencyRates.emptyTitle")} description={t("admin.currencyRates.emptyDescription")} />
      ) : (
        <div className={`${styles.tableWrapper} ${styles.peopleTableWrapper}`}>
          <DataTable
            columns={columns}
            data={state.rates}
            className={styles.peopleDataTable}
            keyField="id"
            ariaLabel={t("admin.currencyRates.title")}
            theme={adminTableTheme}
            defaultSortFieldId="effectiveFrom"
            defaultSortAsc={false}
            pagination
            paginationComponent={TablePager}
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            striped
            highlightOnHover
            columnSeparator="subtle"
          />
        </div>
      )}
    </>
  );
}
